import {
  RoutePlanFeedbackPromptDTO,
  RoutePlanFeedbackSourceEnum,
} from 'api/model';
import {
  useRoutePlanFeedbackAnswerHook,
  useRoutePlanFeedbackDismissPromptHook,
  useRoutePlanFeedbackPromptHook,
  useRoutePlanFeedbackPromptShownHook,
} from 'api/route-plan-feedback/route-plan-feedback';
import useModalStack from 'contexts/modal/useModalStack';
import useRoutePlanFeedbackAccess from 'hooks/useRoutePlanFeedbackAccess';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildFeedbackPageUrl,
  canShowPrompt,
  PROMPT_GATE_CHECK_MS,
  PROMPT_TOAST_DURATION_MS,
  shouldPollForPrompt,
} from './feedback-round-helpers';
import RoutePlanFeedbackToast from './RoutePlanFeedbackToast';

const TOAST_ID = 'route-plan-feedback';

/**
 * Pingar aktiva trafikledare om kvällens ruttprov. Monteras en gång i AppLayout och renderar
 * ingenting själv.
 *
 * En gång i minuten — bara i en synlig flik, för någon som jobbat de senaste fem minuterna — frågar
 * klienten servern om det är dags. Toasten ska aldrig avbryta någon mitt i arbetet: en hämtad fråga
 * visas först efter tio sekunders tystnad och aldrig över en öppen modal, och väntar annars. Servern avgör: bara under rundorna
 * (14:05–15:45, 17:05–18:30 och 07:30–09:00), för alla som får ge feedback utom de som undantagits (inställning 115), högst en gång per eftermiddag,
 * och slumpvis så att inte alla får toasten
 * samtidigt. Se RoutePlanFeedbackRoundLogic.GetPromptAsync.
 */
const RoutePlanFeedbackPrompter = () => {
  const { canGiveFeedback } = useRoutePlanFeedbackAccess();
  const fetchPrompt = useRoutePlanFeedbackPromptHook();
  const dismissPrompt = useRoutePlanFeedbackDismissPromptHook();
  const sendAnswer = useRoutePlanFeedbackAnswerHook();
  const markShown = useRoutePlanFeedbackPromptShownHook();
  const { stack } = useModalStack();
  const location = useLocation();
  const navigate = useNavigate();

  // Allt som ändras vid navigering ligger i refs. Hade det legat i effektens beroenden hade varje
  // sidbyte startat om minuttimern — och den som klickar runt ofta hade aldrig blivit tillfrågad.
  const api = useRef({
    fetchPrompt,
    dismissPrompt,
    sendAnswer,
    markShown,
    navigate,
  });
  api.current = { fetchPrompt, dismissPrompt, sendAnswer, markShown, navigate };
  const lastActivity = useRef(Date.now());
  // Räknas från monteringen, så att en omladdning inte ger en extra lott i lotteriet.
  const lastPoll = useRef(Date.now());
  const pendingPrompt = useRef<RoutePlanFeedbackPromptDTO | null>(null);
  const toastShowing = useRef(false);
  const modalOpen = useRef(false);
  const pathname = useRef(location.pathname);
  modalOpen.current = stack.size > 0;
  pathname.current = location.pathname;

  useEffect(() => {
    const touch = () => {
      lastActivity.current = Date.now();
    };
    window.addEventListener('pointerdown', touch, { passive: true });
    window.addEventListener('keydown', touch, { passive: true });
    window.addEventListener('wheel', touch, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
      window.removeEventListener('wheel', touch);
    };
  }, []);

  useEffect(() => {
    if (!canGiveFeedback) return undefined;

    const show = (prompt: RoutePlanFeedbackPromptDTO) => {
      toastShowing.current = true;
      // Servern räknar frågan som visad först nu — en fråga som hämtats men aldrig syntes (fliken
      // stängdes innan det blev tyst) räknas inte som ignorerad, och lämnas ut igen nästa poll.
      api.current.markShown({ promptId: prompt.promptId }).catch(() => {
        // Når markeringen inte fram förblir frågan "erbjuden" — den räknas då varken som visad
        // eller ignorerad, vilket är det ofarliga felet.
      });
      let answered = false;
      // En utfälld toast läses — den får aldrig försvinna mitt i av tidsgränsen.
      let expanded = false;

      const close = () => {
        toastShowing.current = false;
        toast.dismiss(TOAST_ID);
      };
      const open = (rating?: number) => {
        close();
        api.current.navigate(
          buildFeedbackPageUrl(
            prompt.planningDateString,
            prompt.package.packageIndex,
            rating,
            // Provet toasten gällde — inte dagens senaste, som kan ha ett annat paket med samma nummer.
            prompt.routePlanRunId
          )
        );
      };

      toast.custom(
        (t) => (
          <RoutePlanFeedbackToast
            prompt={prompt}
            visible={t.visible}
            onQuickAnswer={async (rating) => {
              await api.current.sendAnswer({
                planningDateString: prompt.planningDateString,
                packageIndex: prompt.package.packageIndex,
                routePlanRunId: prompt.routePlanRunId,
                rating,
                source: RoutePlanFeedbackSourceEnum.Toast,
                promptId: prompt.promptId,
              });
              answered = true;
            }}
            onOpen={() => open()}
            onMore={() => {
              close();
              api.current.navigate(buildFeedbackPageUrl());
            }}
            onDismiss={() => {
              close();
              if (!answered) {
                api.current
                  .dismissPrompt({ promptId: prompt.promptId })
                  .catch(() => {
                    // "Inte nu" som inte når fram räknas som obesvarad — samma sak för pausregeln.
                  });
              }
            }}
            onClose={close}
            onExpandedChange={(value) => {
              expanded = value;
            }}
          />
        ),
        { id: TOAST_ID, duration: Infinity }
      );

      // Tidsgränsen stänger en toast som ingen rört. Utfälld eller besvarad står den kvar tills
      // användaren själv stänger den.
      window.setTimeout(() => {
        if (!expanded && !answered) close();
      }, PROMPT_TOAST_DURATION_MS);
    };

    const gate = () => ({
      visible: document.visibilityState === 'visible',
      now: Date.now(),
      lastActivity: lastActivity.current,
      pathname: pathname.current,
      modalOpen: modalOpen.current,
    });

    let polling = false;
    const tick = async () => {
      // En hämtad fråga som väntar på tystnad visas först när det är tyst igen.
      if (pendingPrompt.current) {
        if (canShowPrompt(gate())) {
          const prompt = pendingPrompt.current;
          pendingPrompt.current = null;
          show(prompt);
        }
        return;
      }

      if (
        polling ||
        !shouldPollForPrompt({
          ...gate(),
          lastPoll: lastPoll.current,
          toastShowing: toastShowing.current,
        })
      ) {
        return;
      }

      polling = true;
      lastPoll.current = Date.now();
      try {
        const response = await api.current.fetchPrompt();
        if (response.prompt) {
          if (canShowPrompt(gate())) show(response.prompt);
          else pendingPrompt.current = response.prompt;
        }
      } catch {
        // En missad poll är ingen förlust — nästa minut frågar vi igen.
      } finally {
        polling = false;
      }
    };

    const interval = window.setInterval(tick, PROMPT_GATE_CHECK_MS);
    return () => window.clearInterval(interval);
  }, [canGiveFeedback]);

  return null;
};

export default RoutePlanFeedbackPrompter;
